"use strict";

(function () {
  const IDS = {
    widget: "custom-chat-widget",
    closeBtn: "chat-close-btn",
    toggleBtn: "chat-toggle-btn",
    chatBox: "chat-box",
    minimizedIcon: "minimized-widget-icon",
    salesBtn: "chat-sales-btn",
    dynamicName: "dynamic-name",
  };

  const STORAGE_KEYS = {
    minimized: "auChatWidgetMinimized",
    mobileOpen: "auChatWidgetMobileOpen",
  };

  let resizeTimer = null;

  function getElement(id) {
    return document.getElementById(id);
  }

  function isMobileViewport() {
    return window.matchMedia("(max-width: 767px)").matches;
  }

  function setAriaExpanded(expanded) {
    const toggleBtn = getElement(IDS.toggleBtn);
    if (toggleBtn) {
      toggleBtn.setAttribute("aria-expanded", expanded ? "true" : "false");
    }
  }

  function trackWidgetEvent(action, href) {
    const widget = getElement(IDS.widget);
    const payload = {
      event: "chat_widget_click",
      chat_widget_action: action,
      chat_widget_agent: widget?.dataset.agentName || "Marco",
      chat_widget_region: widget?.dataset.region || "AU",
      chat_widget_href: href || "",
    };

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(payload);
    }

    if (typeof window.gtag === "function") {
      window.gtag("event", "chat_widget_click", {
        action,
        agent: payload.chat_widget_agent,
        region: payload.chat_widget_region,
        href: payload.chat_widget_href,
      });
    }
  }

  function updateGreeting() {
    const dynamicName = getElement(IDS.dynamicName);
    const contact = window?.leadData?.contact;
    if (!dynamicName) return;

    if (contact?.firstName) {
      dynamicName.innerText = `Hi ${contact.firstName}, I'm Marco 👋`;
      return;
    }

    dynamicName.innerText = "Hi, I'm Marco 👋";
  }

  function showMinimizedState() {
    const widget = getElement(IDS.widget);
    const minimizedIcon = getElement(IDS.minimizedIcon);
    const chatBox = getElement(IDS.chatBox);

    if (widget) widget.style.display = "none";
    if (minimizedIcon) minimizedIcon.style.display = "block";
    if (chatBox) chatBox.style.display = "none";
    setAriaExpanded(false);
  }

  function applyResponsiveState() {
    const widget = getElement(IDS.widget);
    const toggleBtn = getElement(IDS.toggleBtn);
    const chatBox = getElement(IDS.chatBox);
    const minimizedIcon = getElement(IDS.minimizedIcon);
    const isMinimized = sessionStorage.getItem(STORAGE_KEYS.minimized) === "1";
    const isMobile = isMobileViewport();

    if (!widget || !toggleBtn || !chatBox || !minimizedIcon) return;

    if (isMinimized) {
      showMinimizedState();
      return;
    }

    widget.style.display = "block";
    minimizedIcon.style.display = "none";

    if (isMobile) {
      toggleBtn.style.display = "flex";
      const shouldOpen = sessionStorage.getItem(STORAGE_KEYS.mobileOpen) === "1";
      chatBox.style.display = shouldOpen ? "block" : "none";
      setAriaExpanded(shouldOpen);
      return;
    }

    toggleBtn.style.display = "none";
    chatBox.style.display = "block";
    sessionStorage.removeItem(STORAGE_KEYS.mobileOpen);
    setAriaExpanded(true);
  }

  function toggleChatBox() {
    if (!isMobileViewport()) return;

    const chatBox = getElement(IDS.chatBox);
    if (!chatBox) return;

    const shouldOpen = chatBox.style.display !== "block";
    chatBox.style.display = shouldOpen ? "block" : "none";
    setAriaExpanded(shouldOpen);

    if (shouldOpen) {
      sessionStorage.setItem(STORAGE_KEYS.mobileOpen, "1");
      trackWidgetEvent("toggle_open");
      return;
    }

    sessionStorage.removeItem(STORAGE_KEYS.mobileOpen);
    trackWidgetEvent("toggle_close");
  }

  function minimizeChatWidget() {
    sessionStorage.setItem(STORAGE_KEYS.minimized, "1");
    sessionStorage.removeItem(STORAGE_KEYS.mobileOpen);
    showMinimizedState();
    trackWidgetEvent("minimize");
  }

  function restoreChatWidget() {
    sessionStorage.removeItem(STORAGE_KEYS.minimized);
    applyResponsiveState();
    trackWidgetEvent("restore");
  }

  function handleTrackedClick(event) {
    const target = event.currentTarget;
    const action = target?.dataset.chatAction;
    if (!action) return;
    trackWidgetEvent(action, target.href || "");
  }

  function handleSalesClick(event) {
    handleTrackedClick(event);
    minimizeChatWidget();
  }

  function bindEvents() {
    const closeBtn = getElement(IDS.closeBtn);
    const toggleBtn = getElement(IDS.toggleBtn);
    const minimizedIcon = getElement(IDS.minimizedIcon);
    const salesBtn = getElement(IDS.salesBtn);
    const trackedLinks = document.querySelectorAll("[data-chat-action]");

    closeBtn?.addEventListener("click", minimizeChatWidget);
    toggleBtn?.addEventListener("click", toggleChatBox);
    minimizedIcon?.addEventListener("click", restoreChatWidget);
    salesBtn?.addEventListener("click", handleSalesClick);

    trackedLinks.forEach((link) => {
      if (link.id === IDS.salesBtn) return;
      link.addEventListener("click", handleTrackedClick);
    });

    window.addEventListener("resize", () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(applyResponsiveState, 120);
    });
  }

  function init() {
    if (document.body?.dataset.chatWidgetInitialized === "true") return;
    document.body.dataset.chatWidgetInitialized = "true";

    updateGreeting();
    bindEvents();
    applyResponsiveState();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/* ─────────────────────────────────────────────────────────────────────────
 * Speediance chat widget — enhancement 2026-04-20.4
 *
 * Visual-first attention grab + best-effort audio chime.
 *
 * On first user interaction (scroll/click/touch):
 *   • Widget wiggles
 *   • Pulsing green halo runs around the WhatsApp button until dismissed
 *   • Red "1" notification badge on the WA button (like an unread message)
 *   • Speech-bubble tooltip slides out: "Got questions? I'm here →"
 *   • Audio chime plays (if the browser allows it — Chrome/Safari allow
 *     once a user gesture has occurred, which scroll/click satisfies)
 *
 * All effects stop the instant the user clicks any CTA (they've converted).
 *
 * Also:
 *   • Moves "Chat on WhatsApp" to the TOP of the CTA list
 *   • Renames "Book a Demo With Me" → "Book a Demo/Call With Me" (EN+DE+FR+NL)
 *
 * Debug API: window.chatWidgetEnhance.testBeep() / .testWiggle() / .reset()
 * ─────────────────────────────────────────────────────────────────────── */
(function enhanceChatWidget() {
  "use strict";

  var BEEP_KEY  = "chatWidgetBeeped-v4";
  var BADGE_KEY = "chatWidgetBadge-v4";
  var CONVERTED = "chatWidgetConverted-v4";
  var reducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var LOG_PREFIX = "[chat-widget-enhance]";

  var demoRenames = [
    [/Book a Demo With Me/i,        "Book a Demo/Call With Me"],
    [/Demo mit mir buchen/i,        "Demo oder Anruf mit mir buchen"],
    [/Réserver une démo avec moi/i, "Réserver une démo ou un appel avec moi"],
    [/Boek een demo met mij/i,      "Boek een demo of gesprek met mij"],
  ];

  function log() { try { console.log.apply(console, [LOG_PREFIX].concat([].slice.call(arguments))); } catch (e) {} }

  function applyDemoRename(el) {
    if (!el) return;
    var t = el.textContent;
    for (var i = 0; i < demoRenames.length; i++) {
      if (demoRenames[i][0].test(t)) { el.textContent = t.replace(demoRenames[i][0], demoRenames[i][1]); return; }
    }
  }

  function normaliseAuSalesClass(widget) {
    if (!widget) return;
    var sales = widget.querySelector('.cta-sales');
    if (sales && !widget.querySelector('.cta-whatsapp')) {
      sales.classList.remove('cta-sales');
      sales.classList.add('cta-whatsapp');
      // Replace "Chat with Sales" → "Chat on WhatsApp" (AU store legacy label)
      if (/Chat with Sales/i.test(sales.textContent)) {
        sales.textContent = sales.textContent.replace(/Chat with Sales/i, 'Chat on WhatsApp');
      }
    }
  }

  function reorderCtas(cta) {
    if (!cta) return;
    var wa      = cta.querySelector(".cta-whatsapp");
    var demo    = cta.querySelector(".cta-demo");
    var support = cta.querySelector(".cta-support");
    if (wa      && cta.firstElementChild !== wa)                 cta.insertBefore(wa, cta.firstElementChild);
    if (demo    && wa && demo.previousElementSibling !== wa)     cta.insertBefore(demo, wa.nextElementSibling);
    if (support && cta.lastElementChild !== support)             cta.appendChild(support);
  }

  function injectStyles() {
    if (document.getElementById("chat-widget-enhance-css")) return;
    var style = document.createElement("style");
    style.id = "chat-widget-enhance-css";
    style.appendChild(document.createTextNode(
      "@keyframes chatEnhanceWiggle{" +
      " 0%,100%{transform:rotate(0)}15%{transform:rotate(-10deg)}30%{transform:rotate(9deg)}45%{transform:rotate(-6deg)}60%{transform:rotate(4deg)}78%{transform:rotate(-2deg)}}" +
      ".chat-wiggle{animation:chatEnhanceWiggle 1s cubic-bezier(.36,.07,.19,.97);transform-origin:center}" +
      "@keyframes chatEnhanceHalo{" +
      " 0%{box-shadow:0 0 0 0 rgba(37,211,102,.7)}" +
      " 70%{box-shadow:0 0 0 18px rgba(37,211,102,0)}" +
      " 100%{box-shadow:0 0 0 0 rgba(37,211,102,0)}" +
      "}" +
      ".cta-buttons a.cta-whatsapp.chat-halo{animation:chatEnhanceHalo 1.6s ease-out infinite;position:relative}" +
      ".cta-buttons a.cta-whatsapp{background:linear-gradient(135deg,#25D366,#075E54)!important;box-shadow:0 4px 14px rgba(37,211,102,.45);font-weight:600;position:relative;color:#fff}" +
      ".chat-badge{position:absolute;top:-6px;right:-6px;min-width:20px;height:20px;padding:0 5px;background:#ef4444;color:#fff;font-size:12px;font-weight:800;line-height:20px;border-radius:10px;text-align:center;box-shadow:0 2px 6px rgba(239,68,68,.55);pointer-events:none;animation:chatBadgePop .5s cubic-bezier(.34,1.56,.64,1)}" +
      "@keyframes chatBadgePop{0%{transform:scale(0)}100%{transform:scale(1)}}" +
      "#chat-enhance-bubble{position:absolute;bottom:calc(100% + 14px);right:0;max-width:240px;padding:10px 14px;background:#111827;color:#fff;font-size:13px;line-height:1.35;border-radius:14px 14px 2px 14px;box-shadow:0 8px 24px rgba(0,0,0,.28);opacity:0;transform:translateY(6px) scale(.96);transition:opacity .25s ease,transform .25s ease;pointer-events:auto;z-index:10}" +
      "#chat-enhance-bubble.show{opacity:1;transform:translateY(0) scale(1)}" +
      "#chat-enhance-bubble::after{content:\"\";position:absolute;bottom:-6px;right:18px;width:12px;height:12px;background:#111827;transform:rotate(45deg)}" +
      "#chat-enhance-bubble .close{position:absolute;top:2px;right:6px;background:none;border:0;color:#9ca3af;font-size:16px;line-height:1;cursor:pointer;padding:2px 4px}" +
      "#chat-enhance-bubble .close:hover{color:#fff}" +
      "@media (prefers-reduced-motion: reduce){.chat-wiggle,.cta-buttons a.cta-whatsapp.chat-halo,.chat-badge{animation:none!important}}"
    ));
    (document.head || document.documentElement).appendChild(style);
    log("styles injected");
  }

  // Shared AudioContext — must be created under user gesture
  var sharedCtx = null;
  function ensureAudioCtx() {
    if (sharedCtx) {
      if (sharedCtx.state === "suspended" && typeof sharedCtx.resume === "function") {
        return sharedCtx.resume().then(function () { return sharedCtx; });
      }
      return Promise.resolve(sharedCtx);
    }
    var Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return Promise.reject(new Error("no AudioContext"));
    sharedCtx = new Ctx();
    // iOS/Safari unlock: play a silent one-sample buffer within user gesture
    try {
      var s = sharedCtx.createBufferSource();
      s.buffer = sharedCtx.createBuffer(1, 1, sharedCtx.sampleRate);
      s.connect(sharedCtx.destination);
      s.start(0);
    } catch (e) {}
    if (sharedCtx.state === "suspended" && typeof sharedCtx.resume === "function") {
      return sharedCtx.resume().then(function () { return sharedCtx; });
    }
    return Promise.resolve(sharedCtx);
  }

  function playChime(opts) {
    opts = opts || {};
    if (!opts.force && (sessionStorage.getItem(BEEP_KEY) === "1" || reducedMotion)) return;
    ensureAudioCtx().then(function (ctx) {
      if (!ctx || ctx.state !== "running") { log("audio ctx not running:", ctx && ctx.state); return; }
      var now = ctx.currentTime;
      // Master gain with a compressor so the peaks land LOUD (0.8) without clipping
      var comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -12;
      comp.knee.value = 8;
      comp.ratio.value = 3;
      comp.attack.value = 0.003;
      comp.release.value = 0.12;
      comp.connect(ctx.destination);
      function note(freq, start, dur, peak) {
        var osc = ctx.createOscillator();
        var gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.value = freq;
        osc.connect(gain); gain.connect(comp);
        gain.gain.setValueAtTime(0.0001, now + start);
        gain.gain.exponentialRampToValueAtTime(peak, now + start + 0.012);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
        osc.start(now + start);
        osc.stop(now + start + dur + 0.05);
      }
      // C6 → E6 → G6 — classic ascending triad, loud peaks
      note(1047, 0.00, 0.22, 0.85);
      note(1319, 0.12, 0.26, 0.80);
      note(1568, 0.26, 0.34, 0.75);
      sessionStorage.setItem(BEEP_KEY, "1");
      log("chime played");
    }).catch(function (err) { log("chime error", err && err.message); });
  }

  function isVisible(el) {
    if (!el) return false;
    var cs = window.getComputedStyle(el);
    if (cs.display === "none" || cs.visibility === "hidden") return false;
    return el.offsetWidth > 0 || el.offsetHeight > 0;
  }

  function wiggleWidget() {
    var widget = document.getElementById("custom-chat-widget");
    var toggle = document.getElementById("chat-toggle-btn");
    var mini   = document.getElementById("minimized-widget-icon");
    [widget, toggle, mini].forEach(function (el) {
      if (!isVisible(el)) return;
      el.classList.remove("chat-wiggle");
      void el.offsetWidth;
      el.classList.add("chat-wiggle");
      setTimeout(function () { el.classList.remove("chat-wiggle"); }, 1000);
    });
  }

  function ensureHalo() {
    var wa = document.querySelector("#custom-chat-widget .cta-whatsapp");
    if (wa) wa.classList.add("chat-halo");
  }
  function stopHalo() {
    var wa = document.querySelector("#custom-chat-widget .cta-whatsapp");
    if (wa) wa.classList.remove("chat-halo");
  }

  function ensureBadge() {
    if (sessionStorage.getItem(BADGE_KEY) === "dismissed") return;
    var wa = document.querySelector("#custom-chat-widget .cta-whatsapp");
    if (!wa || wa.querySelector(".chat-badge")) return;
    var badge = document.createElement("span");
    badge.className = "chat-badge";
    badge.setAttribute("aria-hidden", "true");
    badge.textContent = "1";
    wa.appendChild(badge);
  }

  function clearBadge() {
    sessionStorage.setItem(BADGE_KEY, "dismissed");
    var badge = document.querySelector("#custom-chat-widget .cta-whatsapp .chat-badge");
    if (badge) badge.remove();
  }

  var bubbleEl = null;
  function ensureBubble() {
    if (bubbleEl || sessionStorage.getItem("chatWidgetBubbleDismissed-v4") === "1") return;
    var widget = document.getElementById("custom-chat-widget");
    if (!widget) return;
    bubbleEl = document.createElement("div");
    bubbleEl.id = "chat-enhance-bubble";
    bubbleEl.innerHTML = '<button class="close" type="button" aria-label="Dismiss">&times;</button>' +
      '<strong>Got questions?</strong><br>Message us on WhatsApp — we usually reply within a few minutes.';
    widget.appendChild(bubbleEl);
    bubbleEl.querySelector(".close").addEventListener("click", function (e) {
      e.stopPropagation();
      dismissBubble();
    });
    // Show after a small delay so the entrance is noticed
    requestAnimationFrame(function () { setTimeout(function () { bubbleEl.classList.add("show"); }, 200); });
  }

  function dismissBubble() {
    sessionStorage.setItem("chatWidgetBubbleDismissed-v4", "1");
    if (!bubbleEl) return;
    bubbleEl.classList.remove("show");
    setTimeout(function () { if (bubbleEl && bubbleEl.parentNode) bubbleEl.parentNode.removeChild(bubbleEl); bubbleEl = null; }, 260);
  }

  function hasConverted() { return sessionStorage.getItem(CONVERTED) === "1"; }
  function markConverted() { sessionStorage.setItem(CONVERTED, "1"); }

  function fireAttentionBurst() {
    if (hasConverted()) return;
    wiggleWidget();
    ensureHalo();
    ensureBadge();
    ensureBubble();
    playChime();
  }

  function scheduleAttentionNudge() {
    if (hasConverted() || reducedMotion) { log("skip nudge"); return; }

    var userInteracted = false;
    var firstTimer = null;
    var repeatTimer = null;

    function onInteract() {
      if (userInteracted) return;
      userInteracted = true;
      log("first interaction seen");
      ensureAudioCtx(); // warm up audio context inside gesture
      firstTimer = setTimeout(function () {
        if (hasConverted()) return;
        fireAttentionBurst();
      }, 1000);

      // Persistent silent wiggles at 45s and 90s
      repeatTimer = setTimeout(function rep1() {
        if (hasConverted()) return;
        wiggleWidget();
        repeatTimer = setTimeout(function rep2() {
          if (hasConverted()) return;
          wiggleWidget();
        }, 45000);
      }, 45000);
    }

    ["click", "scroll", "touchstart", "keydown", "pointerdown"].forEach(function (ev) {
      window.addEventListener(ev, onInteract, { passive: true, once: true });
    });

    // On CTA click → stop everything
    document.querySelectorAll("[data-chat-action]").forEach(function (el) {
      el.addEventListener("click", function () {
        markConverted();
        if (firstTimer) clearTimeout(firstTimer);
        if (repeatTimer) clearTimeout(repeatTimer);
        stopHalo();
        clearBadge();
        dismissBubble();
        log("converted — nudges off");
      }, { once: true });
    });
  }

  var applied = false;
  function apply() {
    var widget = document.getElementById("custom-chat-widget");
    if (!widget) return false;
    var cta = widget.querySelector(".cta-buttons");
    if (!cta) return false;
    injectStyles();
    normaliseAuSalesClass(widget);
    reorderCtas(cta);
    applyDemoRename(cta.querySelector(".cta-demo"));
    var demo = cta.querySelector(".cta-demo");
    if (demo && !demo.__chatRenameObs) {
      var mo = new MutationObserver(function () { applyDemoRename(demo); });
      mo.observe(demo, { characterData: true, childList: true, subtree: true });
      demo.__chatRenameObs = mo;
    }
    if (!applied) {
      applied = true;
      scheduleAttentionNudge();
      log("apply complete");
    }
    return true;
  }

  function start() {
    if (apply()) return;
    var attempts = 0;
    var iv = setInterval(function () {
      attempts += 1;
      if (apply() || attempts > 50) clearInterval(iv);
    }, 100);
    log("polling for widget");
  }

  // Debug API
  window.chatWidgetEnhance = {
    testBeep:   function () { playChime({ force: true }); },
    testWiggle: function () { wiggleWidget(); },
    fire:       function () { fireAttentionBurst(); },
    reset:      function () {
      sessionStorage.removeItem(BEEP_KEY);
      sessionStorage.removeItem(BADGE_KEY);
      sessionStorage.removeItem(CONVERTED);
      sessionStorage.removeItem("chatWidgetBubbleDismissed-v4");
      log("session state cleared");
    },
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})();
